import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { platformActions } from "../store/platform-slice";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import useTheme from "../../../common/hooks/useTheme";
import { useAppStore } from "../../../providers/AppStore";
import PlatformSidebar from "../components/navigation/PlatformSidebar";
import {
  SidebarProvider,
  useSidebar,
} from "../components/navigation/SidebarContext";
import PageLoader from "../../reports/components/UI/PageLoader";
import { Box, useMediaQuery } from "@mui/material";
import SecondaryButton from "../../../common/ui/SecondaryButton";
import { useAuth } from "../../../providers/AuthProvider";
import { isMobile } from "react-device-detect";
import SuccessPageQuestionnaire from "../../questionnaire/components/pages/SuccessPageQuestionnaire";
import ClientSidePanel from "../components/common/ClientSidePanel";
import { useClientSidePanel } from "../../../providers/ClientSidePanelProvider";
import ScoreSidePanel from "../components/score/ScoreSidePanel";
import { useScoreSidePanel } from "../components/score/ScoreSidePanelContext";
import Breadcrumb from "../components/common/Breadcrumb";
import { useElevenLabsWidget } from "../../reports/hooks/useElevenLabsWidget";
import { ChatRoot, resolveChatProviderMode } from "../../chat";
import PlatformSnackbars from "./PlatformSnackbars";
import {
  SubjectErrorKind,
  SubjectMode,
  SubjectProvider,
  useSubject,
} from "../providers/SubjectProvider";

const ERROR_COPY: Record<
  SubjectErrorKind,
  { title: string; message: (email: string | null) => string }
> = {
  missing_id: {
    title: "No client selected",
    message: () =>
      "This page needs a client. Open a client from the client list.",
  },
  load_failed: {
    title: "Unable to load client",
    message: () =>
      "The client could not be loaded. They may not exist, may not be registered yet, or you may not have access to them.",
  },
  questionnaire_incomplete: {
    title: "No Biometric Data Available",
    message: (email) =>
      `The customer with email ${
        email || ""
      }, has not completed the biometric questionnaire yet. Please inform them to complete it.`,
  },
};

const WorkspaceError = ({
  errorKind,
  errorEmail,
}: {
  errorKind: SubjectErrorKind;
  errorEmail: string | null;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const copy = ERROR_COPY[errorKind];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        flex: 1,
        padding: "48px 24px",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          fontSize: "16px",
          fontFamily: "var(--font-Bold)",
          color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
        }}
      >
        {copy.title}
      </Box>
      <Box
        sx={{
          fontSize: "12px",
          fontFamily: "var(--font-Medium)",
          color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
          maxWidth: "420px",
        }}
      >
        {copy.message(errorEmail)}
      </Box>
      <SecondaryButton
        label={"Go to client list"}
        onClick={() => navigate("/client/client-list")}
      />
    </Box>
  );
};

const WorkspaceShellContent = ({ mode }: { mode: SubjectMode }) => {
  const theme = useTheme();
  const { sidebarWidth } = useSidebar();
  const { ASisLoadingPlatformData } = useAppStore();
  const { AuthUser, AuthIsSimpleUser } = useAuth();
  const { status, errorKind, errorEmail, subjectId } = useSubject();
  const { isOpen, closePanel } = useClientSidePanel();
  const { closePanel: closeScorePanel } = useScoreSidePanel();
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery("(min-width:601px)");

  const location = useLocation();
  const chatProviderMode = resolveChatProviderMode(location.search);
  const isMockChatMode = chatProviderMode === "mock";
  const isElevenLabsMode = !isMockChatMode;
  const scrollableRef = useRef<HTMLDivElement>(null);

  const isClientMode = mode === "client";

  // The ClientSidePanel exists only in the client workspace (affiliate acting
  // on a client); the customer's own view never shows it.
  useEffect(() => {
    if (!isClientMode) {
      closePanel();
    }
  }, [isClientMode, closePanel]);

  // Close score side panel and clear selected system when navigating away from the score screen
  useEffect(() => {
    if (!location.pathname.includes("/score")) {
      closeScorePanel();
      dispatch(platformActions.setSelectedSystem(0));
    }
  }, [location.pathname, closeScorePanel, dispatch]);

  useEffect(() => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTop = 0;
    }
  }, [location]);

  // Always call the hook - pass agent type string and loading state
  useElevenLabsWidget(
    "",
    AuthUser.agentType,
    isClientMode ? subjectId : null,
    status === "ready" && !ASisLoadingPlatformData,
    isElevenLabsMode
  );

  if (AuthIsSimpleUser && isMobile) {
    return <SuccessPageQuestionnaire />;
  }

  const renderContent = () => {
    if (status === "error" && errorKind) {
      return <WorkspaceError errorKind={errorKind} errorEmail={errorEmail} />;
    }
    if (status === "loading" || ASisLoadingPlatformData) {
      return <PageLoader />;
    }
    return <Outlet />;
  };

  return (
    <div
      className="viewport-height"
      style={{
        display: "flex",
        backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
      }}
    >
      <PlatformSidebar variant={isClientMode ? "client" : "self"} />
      {isElevenLabsMode && <div id="chat-container"></div>}
      {isMockChatMode && (
        <ChatRoot surfaceId="platform" providerMode={chatProviderMode} />
      )}
      <div
        ref={scrollableRef}
        className="viewport-height"
        style={{
          flex: 1,
          marginLeft: `${sidebarWidth}px`,
          overflowX: "auto",
          overflowY: "auto",
          backgroundColor: theme.colorTokens.N[10],
          transition: "margin-left 0.3s ease-in-out",
        }}
      >
        <Box
          sx={{
            width: "100%",
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            marginRight: isDesktop && isOpen && isClientMode ? "600px" : "0",
            transition: "margin-right 0.3s ease-in-out",
          }}
        >
          {/* Breadcrumb navigation */}
          <Breadcrumb variant={isClientMode ? "client" : "self"} />

          {/* Main content */}
          <Box
            sx={{
              width: `${1440 - sidebarWidth}px`,
              padding: "24px 16px",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {renderContent()}
          </Box>
        </Box>
      </div>
      {/* Client Side Panel - client workspace only */}
      {isClientMode && <ClientSidePanel />}
      {/* Score Side Panel */}
      <ScoreSidePanel />
      <PlatformSnackbars />
    </div>
  );
};

/**
 * The single-subject workspace shell. Serves both the customer's own view
 * (mode="self", /platform routes) and the affiliate's per-client browser tab
 * (mode="client", /client workspace routes) — same layout, same pages, with
 * the subject resolved by SubjectProvider.
 */
const WorkspaceShell = ({ mode }: { mode: SubjectMode }) => {
  return (
    <SidebarProvider>
      <SubjectProvider mode={mode}>
        <WorkspaceShellContent mode={mode} />
      </SubjectProvider>
    </SidebarProvider>
  );
};

export default WorkspaceShell;
